import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight, Plus, Check, X, Clock, User, Stethoscope, RefreshCw } from 'lucide-react'
import { Button } from '@components/ui/button'
import { Badge } from '@components/ui/badge'
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@components/ui/sheet'
import { useToast } from '@hooks/use-toast'
import { useAuthStore } from '@hooks/useAuthStore'
import { appointmentApi } from '@services/appointment.service'
import { CreateAppointmentDialog } from '@components/appointments/CreateAppointmentDialog'
import { AppointmentNotificationStatus } from '@components/appointments/AppointmentNotificationStatus'
import { DEFAULT_CLINIC_TZ } from '@lib/clinicDate'
import { Appointment } from '@/types'
import { cn } from '@lib/utils'
import { canDecideAppointment } from '@lib/appointmentPermissions'

// ─── Agenda semanal ──────────────────────────────────────────────────────────
// Pantalla principal de la clínica: la semana de un vistazo, cada cita en su
// hora real (zona de la clínica), estado por color, y acciones sin salir de
// aquí. Se refresca sola cada 20 s para reflejar lo que entra por WhatsApp.

const TZ = DEFAULT_CLINIC_TZ
const DAY_START = 8   // 08:00
const DAY_END = 20    // 20:00
const HOUR_PX = 64
const DAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']
const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']

const STATUS: Record<string, { bar: string; bg: string; text: string; label: string }> = {
  pending_approval: { bar: 'bg-amber-400',   bg: 'bg-amber-50 hover:bg-amber-100',     text: 'text-amber-900',   label: 'Pendiente' },
  scheduled:        { bar: 'bg-sky-500',     bg: 'bg-sky-50 hover:bg-sky-100',         text: 'text-sky-900',     label: 'Programada' },
  confirmed:        { bar: 'bg-emerald-500', bg: 'bg-emerald-50 hover:bg-emerald-100', text: 'text-emerald-900', label: 'Confirmada' },
  checked_in:       { bar: 'bg-cyan-500',    bg: 'bg-cyan-50 hover:bg-cyan-100',       text: 'text-cyan-900',    label: 'En espera' },
  in_progress:      { bar: 'bg-violet-500',  bg: 'bg-violet-50 hover:bg-violet-100',   text: 'text-violet-900',  label: 'En consulta' },
  completed:        { bar: 'bg-slate-400',   bg: 'bg-slate-50 hover:bg-slate-100',     text: 'text-slate-700',   label: 'Completada' },
  rescheduled:      { bar: 'bg-orange-500',  bg: 'bg-orange-50 hover:bg-orange-100',   text: 'text-orange-900',  label: 'Reprogramada' },
  cancelled:        { bar: 'bg-red-300',     bg: 'bg-red-50/60',                       text: 'text-red-700 line-through', label: 'Cancelada' },
  no_show:          { bar: 'bg-slate-300',   bg: 'bg-slate-50',                        text: 'text-slate-500 line-through', label: 'No asistió' },
  rejected:         { bar: 'bg-red-300',     bg: 'bg-red-50/60',                       text: 'text-red-700 line-through', label: 'Rechazada' },
}
const HIDDEN = new Set(['cancelled', 'rejected'])

// Partes de fecha en la zona de la clínica (nunca la del navegador).
function tzParts(iso: string) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false, weekday: 'short',
  }).formatToParts(new Date(iso))
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? ''
  const wd = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(get('weekday'))
  return {
    ymd: `${get('year')}-${get('month')}-${get('day')}`,
    hour: Number(get('hour')) % 24, minute: Number(get('minute')),
    dow: (wd + 6) % 7, // 0 = lunes
  }
}
function fmtTime(iso: string) {
  return new Intl.DateTimeFormat('es-MX', { timeZone: TZ, hour: 'numeric', minute: '2-digit', hour12: true }).format(new Date(iso)).replace('a. m.', 'am').replace('p. m.', 'pm')
}
function todayYmd() { return tzParts(new Date().toISOString()).ymd }
function addDays(ymd: string, n: number) {
  const [y, m, d] = ymd.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d + n))
  return dt.toISOString().slice(0, 10)
}
function mondayOf(ymd: string) {
  const [y, m, d] = ymd.split('-').map(Number)
  const dow = (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7
  return addDays(ymd, -dow)
}
function dayNumber(ymd: string) { return Number(ymd.slice(8, 10)) }
function monthLabel(ymd: string) { return MONTHS[Number(ymd.slice(5, 7)) - 1] }
function isoRangeForWeek(monday: string) {
  // La API filtra en UTC; abrimos la ventana con holgura y colocamos por tz.
  return { from: `${addDays(monday, -1)}T00:00:00Z`, to: `${addDays(monday, 7)}T23:59:59Z` }
}

export function AgendaPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { toast } = useToast()
  const user = useAuthStore((s) => s.user)
  const canManage = ['doctor', 'admin', 'super_admin'].includes(user?.role ?? '')

  const [monday, setMonday] = useState(() => mondayOf(todayYmd()))
  const [view, setView] = useState<'week' | 'day'>(() => (typeof window !== 'undefined' && window.innerWidth < 900 ? 'day' : 'week'))
  const [dayCursor, setDayCursor] = useState(() => todayYmd())
  const [selected, setSelected] = useState<Appointment | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const today = todayYmd()
  const days = useMemo(() => (view === 'day' ? [dayCursor] : Array.from({ length: 6 }, (_, i) => addDays(monday, i))), [monday, view, dayCursor])
  const dayLabels = view === 'day' ? [DAYS[(new Date(dayCursor + 'T12:00:00Z').getUTCDay() + 6) % 7] ?? 'Dom'] : DAYS
  const goPrev = () => (view === 'day' ? setDayCursor(addDays(dayCursor, -1)) : setMonday(addDays(monday, -7)))
  const goNext = () => (view === 'day' ? setDayCursor(addDays(dayCursor, 1)) : setMonday(addDays(monday, 7)))
  const goToday = () => { setMonday(mondayOf(today)); setDayCursor(today) }

  const range = view === 'day' ? { from: `${addDays(dayCursor, -1)}T00:00:00Z`, to: `${addDays(dayCursor, 1)}T23:59:59Z` } : isoRangeForWeek(monday)
  const { data, isFetching, isError, dataUpdatedAt, refetch } = useQuery({
    queryKey: ['agenda', view, view === 'day' ? dayCursor : monday],
    queryFn: () => appointmentApi.getAppointments({ date_from: range.from, date_to: range.to, limit: 500 }),
    refetchInterval: 20_000,
  })
  const appointments = useMemo(
    () => (isError ? [] : (data?.items ?? [])),
    [data, isError]
  )

  useEffect(() => {
    if (isError) setSelected(null)
  }, [isError])

  const byDay = useMemo(() => {
    const map = new Map<string, (Appointment & { _p: ReturnType<typeof tzParts> })[]>()
    for (const a of appointments) {
      if (HIDDEN.has(a.status)) continue
      const p = tzParts(a.appointment_date)
      if (!map.has(p.ymd)) map.set(p.ymd, [])
      map.get(p.ymd)!.push({ ...a, _p: p })
    }
    return map
  }, [appointments])

  const pending = appointments.filter((a) => a.status === 'pending_approval')

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['agenda'] })
    qc.invalidateQueries({ queryKey: ['pending-approvals'] })
  }
  const approve = useMutation({
    mutationFn: (id: string) => appointmentApi.approveAppointment(id),
    onSuccess: () => { toast({ title: 'Cita aceptada' }); setSelected(null); invalidate() },
    onError: () => toast({ title: 'No se pudo aceptar la cita', variant: 'destructive' }),
  })
  const reject = useMutation({
    mutationFn: (id: string) => appointmentApi.rejectAppointment(id, { rejection_reason: 'Rechazada desde la agenda' }),
    onSuccess: () => { toast({ title: 'Cita rechazada' }); setSelected(null); invalidate() },
    onError: () => toast({ title: 'No se pudo rechazar la cita', variant: 'destructive' }),
  })
  const cancel = useMutation({
    mutationFn: (id: string) => appointmentApi.updateAppointment(id, { status: 'cancelled' } as never),
    onSuccess: () => { toast({ title: 'Cita cancelada' }); setSelected(null); invalidate() },
    onError: () => toast({ title: 'No se pudo cancelar la cita', variant: 'destructive' }),
  })

  const hours = Array.from({ length: DAY_END - DAY_START + 1 }, (_, i) => DAY_START + i)
  const nowP = tzParts(new Date().toISOString())
  const nowTop = (nowP.hour - DAY_START) * HOUR_PX + (nowP.minute / 60) * HOUR_PX
  const last = days[days.length - 1]
  const weekLabel = view === 'day'
    ? new Intl.DateTimeFormat('es-MX', { timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long' }).format(new Date(dayCursor + 'T12:00:00Z'))
    : monthLabel(days[0]) === monthLabel(last)
      ? `${dayNumber(days[0])} – ${dayNumber(last)} ${monthLabel(last)} ${last.slice(0, 4)}`
      : `${dayNumber(days[0])} ${monthLabel(days[0])} – ${dayNumber(last)} ${monthLabel(last)} ${last.slice(0, 4)}`
  const patientName = (a: Appointment) => a.patient ? `${a.patient.first_name} ${a.patient.last_name}` : (a.patient_name || 'Paciente')
  const doctorName = (a: Appointment) => a.doctor ? `Dr. ${a.doctor.first_name} ${a.doctor.last_name}` : (a.doctor_name ? `Dr. ${a.doctor_name}` : 'Médico')
  const updatedLabel = dataUpdatedAt ? new Intl.DateTimeFormat('es-MX', { timeZone: TZ, hour: '2-digit', minute: '2-digit', second: '2-digit' }).format(new Date(dataUpdatedAt)) : ''

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col gap-4">
      {/* Cabecera */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Agenda</p>
          <h1 className="font-serif text-2xl sm:text-3xl leading-tight text-primary break-words">{weekLabel}</h1>
        </div>
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          <span className="hidden items-center gap-1 text-xs text-muted-foreground sm:flex">
            <RefreshCw className={cn('h-3 w-3', isFetching && 'animate-spin')} /> {updatedLabel}
          </span>
          <div className="flex overflow-hidden rounded-md border">
            <button className={cn('px-3 py-1.5 text-xs', view === 'day' ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground')} onClick={() => setView('day')}>Día</button>
            <button className={cn('px-3 py-1.5 text-xs', view === 'week' ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground')} onClick={() => setView('week')}>Semana</button>
          </div>
          <Button variant="outline" size="sm" onClick={goToday}>Hoy</Button>
          <Button variant="outline" size="icon" aria-label="Anterior" onClick={goPrev}><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="icon" aria-label="Siguiente" onClick={goNext}><ChevronRight className="h-4 w-4" /></Button>
          <Button size="sm" className="basis-full sm:basis-auto" onClick={() => setCreateOpen(true)}><Plus className="mr-1 h-4 w-4" /> Nueva cita</Button>
        </div>
      </div>

      {/* Pendientes de aprobación */}
      {!isError && pending.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3">
          <span className="text-sm font-medium text-amber-900">{pending.length} {pending.length === 1 ? 'solicitud pendiente' : 'solicitudes pendientes'} de aprobación interna</span>
          <div className="flex flex-wrap gap-2">
            {pending.map((a) => (
              <button key={a.id} onClick={() => setSelected(a)} className="rounded-full border border-amber-300 bg-white px-3 py-1 text-xs text-amber-900 hover:bg-amber-100">
                {patientName(a)} · {fmtTime(a.appointment_date)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Grilla semanal */}
      {isError ? (
        <div role="alert" className="flex min-h-[18rem] flex-1 flex-col items-center justify-center rounded-2xl border border-destructive/20 bg-destructive/5 px-6 text-center">
          <p className="font-medium text-foreground">{data ? 'No se pudo actualizar la agenda.' : 'No se pudo cargar la agenda.'}</p>
          <p className="mt-1 text-sm text-muted-foreground">Los datos anteriores se ocultaron para evitar operar sobre información desactualizada.</p>
          <Button type="button" variant="outline" className="mt-4" onClick={() => { void refetch() }}>Reintentar agenda</Button>
        </div>
      ) : !data && isFetching ? (
        <div role="status" className="flex min-h-[18rem] flex-1 items-center justify-center rounded-2xl border bg-card text-sm text-muted-foreground">
          <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> Cargando agenda…
        </div>
      ) : <div className="min-h-0 flex-1 overflow-auto rounded-2xl border bg-card">
        <div className={cn('grid', view === 'week' && 'min-w-[880px]')} style={{ gridTemplateColumns: `56px repeat(${days.length}, minmax(0, 1fr))` }}>
          {/* encabezado de días */}
          <div className="sticky top-0 z-20 border-b bg-card" />
          {days.map((d, i) => (
            <div key={d} className={cn('sticky top-0 z-20 border-b border-l bg-card px-3 py-2', d === today && 'bg-primary/5')}>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{dayLabels[i]}</div>
              <div className={cn('text-2xl font-semibold leading-none', d === today ? 'text-primary' : 'text-foreground')}>{dayNumber(d)}</div>
              <div className="text-[11px] text-muted-foreground">{byDay.get(d)?.length ?? 0} {byDay.get(d)?.length === 1 ? 'cita' : 'citas'}</div>
            </div>
          ))}

          {/* columna de horas */}
          <div className="relative" style={{ height: hours.length * HOUR_PX }}>
            {hours.map((h) => (
              <div key={h} className="absolute right-2 -translate-y-1/2 text-[11px] tabular-nums text-muted-foreground" style={{ top: (h - DAY_START) * HOUR_PX }}>
                {h % 12 === 0 ? 12 : h % 12}{h < 12 ? 'am' : 'pm'}
              </div>
            ))}
          </div>

          {/* columnas de días */}
          {days.map((d) => (
            <div key={d} className={cn('relative border-l', d === today && 'bg-primary/[0.03]')} style={{ height: hours.length * HOUR_PX }}>
              {hours.map((h) => (
                <div key={h} className="absolute inset-x-0 border-t border-dashed border-border/70" style={{ top: (h - DAY_START) * HOUR_PX }} />
              ))}
              {d === today && nowTop >= 0 && nowTop <= hours.length * HOUR_PX && (
                <div className="absolute inset-x-0 z-10 flex items-center" style={{ top: nowTop }}>
                  <span className="-ml-1 h-2 w-2 rounded-full bg-red-500" /><span className="h-px flex-1 bg-red-500" />
                </div>
              )}
              {(byDay.get(d) ?? []).map((a) => {
                const top = (a._p.hour - DAY_START) * HOUR_PX + (a._p.minute / 60) * HOUR_PX
                const height = Math.max(28, ((a.duration_minutes || 30) / 60) * HOUR_PX - 2)
                const st = STATUS[a.status] ?? STATUS.scheduled
                return (
                  <button
                    key={a.id}
                    onClick={() => setSelected(a)}
                    title={`${patientName(a)} · ${fmtTime(a.appointment_date)} · ${a.reason}`}
                    className={cn('absolute inset-x-1 z-[5] overflow-hidden rounded-md border border-black/5 text-left shadow-sm transition', st.bg)}
                    style={{ top, height }}
                  >
                    <span className={cn('absolute inset-y-0 left-0 w-1', st.bar)} />
                    <div className={cn('pl-2.5 pr-1 pt-1 text-[12px] font-semibold leading-tight', st.text)}>{patientName(a)}</div>
                    <div className="truncate pl-2.5 pr-1 text-[11px] text-muted-foreground">{fmtTime(a.appointment_date)} · {a.reason}</div>
                  </button>
                )
              })}
            </div>
          ))}
        </div>
      </div>}

      {/* Leyenda */}
      <div className="flex flex-wrap gap-4 text-[11px] text-muted-foreground">
        {['pending_approval', 'scheduled', 'confirmed', 'in_progress', 'completed'].map((s) => (
          <span key={s} className="flex items-center gap-1.5"><span className={cn('h-2.5 w-2.5 rounded-sm', STATUS[s].bar)} />{STATUS[s].label}</span>
        ))}
      </div>

      {/* Panel de detalle */}
      <Sheet open={!isError && !!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-[380px] sm:max-w-[420px]">
          {!isError && selected && (() => {
            const st = STATUS[selected.status] ?? STATUS.scheduled
            const end = new Date(new Date(selected.appointment_date).getTime() + (selected.duration_minutes || 30) * 60000).toISOString()
            return (
              <div className="flex h-full flex-col gap-5">
                <SheetHeader>
                  <Badge className={cn('w-fit', st.bg.split(' ')[0], st.text, 'border-0')}>{st.label}</Badge>
                  <SheetTitle className="font-serif text-2xl">{patientName(selected)}</SheetTitle>
                  <SheetDescription>{selected.reason}</SheetDescription>
                </SheetHeader>
                <dl className="grid gap-3 text-sm">
                  <div className="flex items-center gap-3"><Clock className="h-4 w-4 text-muted-foreground" /><span>{new Intl.DateTimeFormat('es-MX', { timeZone: TZ, weekday: 'long', day: 'numeric', month: 'long' }).format(new Date(selected.appointment_date))}, {fmtTime(selected.appointment_date)} – {fmtTime(end)}</span></div>
                  <div className="flex items-center gap-3"><Stethoscope className="h-4 w-4 text-muted-foreground" /><span>{doctorName(selected)}</span></div>
                  {selected.patient?.phone_primary && <div className="flex items-center gap-3"><User className="h-4 w-4 text-muted-foreground" /><span>{selected.patient.phone_primary}</span></div>}
                </dl>
                <AppointmentNotificationStatus appointmentId={selected.id} />
                <div className="mt-auto flex flex-col gap-2">
                  {canDecideAppointment(user, selected) && selected.status === 'pending_approval' && (
                    <div className="flex gap-2">
                      <Button className="flex-1" onClick={() => approve.mutate(selected.id)} disabled={approve.isPending}><Check className="mr-1 h-4 w-4" /> Aceptar</Button>
                      <Button variant="outline" className="flex-1" onClick={() => reject.mutate(selected.id)} disabled={reject.isPending}><X className="mr-1 h-4 w-4" /> Rechazar</Button>
                    </div>
                  )}
                  <Button variant="outline" onClick={() => navigate(`/workspace/patient/${selected.patient_id}/historial-citas`)}>Ver expediente</Button>
                  {canManage && !['cancelled', 'completed', 'rejected'].includes(selected.status) && (
                    <Button variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => cancel.mutate(selected.id)} disabled={cancel.isPending}>Cancelar cita</Button>
                  )}
                </div>
              </div>
            )
          })()}
        </SheetContent>
      </Sheet>

      <CreateAppointmentDialog open={createOpen} onOpenChange={setCreateOpen} onSuccess={invalidate} />
    </div>
  )
}
